import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { baseurl, imgurl } from "../../baseurl";
import axios from "axios";
import {
  FiArrowLeft,
  FiPackage,
  FiTruck,
  FiUser,
  FiCreditCard,
  FiMapPin,
  FiCalendar,
} from "react-icons/fi";
import Swal from "sweetalert2";
import { TiTick } from "react-icons/ti";

function OrderView() {
  const { order_uuid } = useParams();
  const vendorId = localStorage.getItem("vendorId");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // helper: returns the next status in the flow or null if none
  const getNextStatus = (current) => {
    const flow = ["Pending", "Confirmed", "Ready To Ship",  "Shipped", "Delivered"];
    const idx = flow.indexOf(current);
    if (idx === -1) return null;
    return flow[idx + 1] || null;
  };

  // 🟢 Fetch single order details
  const fetchOrder = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${baseurl}getOrderDetailsForVendor/${vendorId}?order_uuid=${order_uuid}`
      );
      if (res.data.success) {
        setOrder(res.data.order);
      } else {
        setOrder(null);
      }
    } catch (err) {
      console.error("Error fetching order details:", err);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vendorId && order_uuid) fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, order_uuid]);

  const handleAdvanceStatus = async () => {
    if (!order) return;

    const next = getNextStatus(order.order_status);
    if (!next) {
      Swal.fire({
        icon: "info",
        title: "No further steps",
        text: "Order cannot be advanced further.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Confirm Status Update",
      html: `Move order <b>${order.order_uuid}</b> <br/>from <b>${order.order_status}</b> → <b>${next}</b>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
    });

    if (!result.isConfirmed) return;

    try {
      setUpdating(true);

      const res = await axios.put(
        `${baseurl}updateOrderStatus/${vendorId}?order_uuid=${order.order_uuid}`,
        { new_status: next }
      );

      if (res.data?.success) {
        // Optimistic UI update
        setOrder((prev) =>
          prev
            ? {
                ...prev,
                order_status: next,
                updated_at: new Date().toISOString(),
              }
            : prev
        );

        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: `Order advanced to "${next}"`,
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
      } else {
        const msg = res.data?.message || "Failed to update status";
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: msg,
          showConfirmButton: false,
          timer: 2000,
        });
        await fetchOrder();
      }
    } catch (err) {
      console.error("❌ Error updating order status:", err);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Error updating status. Please try again.",
        showConfirmButton: false,
        timer: 2000,
      });
      await fetchOrder();
    } finally {
      setUpdating(false);
    }
  };





  const handleCancelOrder = async () => {
  if (!order || order.order_status === "Cancelled") return;

  const result = await Swal.fire({
    title: "Are you sure?",
    text: "Do you really want to cancel this order? This action cannot be undone.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, Cancel Order",
    cancelButtonText: "No, Keep it",
    confirmButtonColor: "#d33", // Red for cancellation
    cancelButtonColor: "#3085d6",
  });

  if (result.isConfirmed) {
    try {
      setUpdating(true); // Assuming you have an 'updating' state
      const { data } = await axios.put(`${baseurl}updateOrderStatus/${order.order_id}`, {
        status: "Cancelled"
      });

      if (data.success) {
        Swal.fire("Cancelled!", "The order has been cancelled.", "success");
        // Update local state or re-fetch order details
        setOrder(prev => ({ ...prev, order_status: "Cancelled" }));
      } else {
        Swal.fire("Error", data.message || "Failed to cancel order", "error");
      }
    } catch (err) {
      console.error("Error cancelling order:", err);
      Swal.fire("Error", "A server error occurred while cancelling the order.", "error");
    } finally {
      setUpdating(false);
    }
  }
};






  const handleMarkPaid = async () => {
    if (!order || order.payment_status !== "Pending") return;

    const result = await Swal.fire({
      title: "Mark Payment as Paid?",
      html: `Mark payment for order <b>${order.order_uuid}</b> as <b>Paid</b>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, mark as Paid",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
    });

    

    if (!result.isConfirmed) return;

    try {
      setUpdating(true);
      const res = await axios.post(
        `${baseurl}updatePaymentStatus?order_uuid=${order.order_uuid}`,
        { new_status: "Paid" }
      );

      if (res.data?.success) {
        // Optimistic UI update
        setOrder((prev) => (prev ? { ...prev, payment_status: "Paid" } : prev));

        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Payment marked as Paid",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
      } else {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: res.data?.message || "Failed to update payment status",
          showConfirmButton: false,
          timer: 2000,
        });
        await fetchOrder();
      }
    } catch (err) {
      console.error("❌ Error updating payment status:", err);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Error updating payment. Try again.",
        showConfirmButton: false,
        timer: 2000,
      });
      await fetchOrder();
    } finally {
      setUpdating(false);
    }
  };

  if (loading)
    return (
      <>
        <Topbar />
        <Sidebar />
        <main className="main-wrapper p-4 text-center">
          <div className="spinner-border text-primary" role="status"></div>
          <div className="mt-2 text-muted">Loading order details...</div>
        </main>
      </>
    );

  if (!order)
    return (
      <>
        <Topbar />
        <Sidebar />
        <main className="main-wrapper p-4">
          <div className="text-center text-danger fw-semibold">
            Order not found or inaccessible
          </div>
        </main>
      </>
    );

  const nextStatus = getNextStatus(order.order_status);
  const canAdvance = Boolean(nextStatus);
  const canMarkPaid = order?.payment_status === "Pending";
  const statusFlow = ["Pending", "Confirmed", "Ready To Ship",  "Shipped", "Delivered"];

  return (
    <>
      <Topbar />
      <Sidebar />

      <main
        className="main-wrapper p-3 p-lg-4"
        style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}
      >
        {/* Page Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
          <div>
            <Link
              to="/all-orders"
              className="text-decoration-none text-muted small d-flex align-items-center gap-1 mb-1"
            >
              <FiArrowLeft /> Back to Orders
            </Link>
            <h4 className="fw-bold m-0 d-flex align-items-center gap-2">
              Order{" "}
              <span className="text-dark">#{order.order_uuid.slice(0, 8)}</span>
            </h4>
          </div>

          <div className="d-flex gap-2">

         

            <button
                className="btn btn-outline-success d-flex align-items-center gap-2 shadow-sm"
                onClick={handleCancelOrder}
                disabled={updating}
              >
                <FiCreditCard /> Cancel Order
              </button>


            {canMarkPaid && (
              <button
                className="btn btn-outline-success d-flex align-items-center gap-2 shadow-sm"
                onClick={handleMarkPaid}
                disabled={updating}
              >
                <FiCreditCard /> Mark Paid
              </button>
            )}
            <button
              className={`btn ${canAdvance ? "btn-primary" : "btn-light text-muted"} d-flex align-items-center gap-2 shadow-sm`}
              onClick={handleAdvanceStatus}
              disabled={!canAdvance || updating}
            >
              <TiTick size={20} />
              {updating
                ? "Updating..."
                : canAdvance
                  ? `Move to ${nextStatus}`
                  : "Order Completed"}
            </button>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-8">
            {/* Status Tracker Card */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="fw-bold m-0">Order Journey</h6>
                  <span
                    className={`badge rounded-pill px-3 py-2 ${
                      order.order_status === "Delivered"
                        ? "bg-success"
                        : "bg-primary-subtle text-primary"
                    }`}
                    style={{ fontSize: "0.8rem" }}
                  >
                    {order.order_status}
                  </span>
                </div>

                <div className="d-flex justify-content-between position-relative mb-2">
                  {statusFlow.map((s, idx) => {
                    const currentIdx = statusFlow.indexOf(order.order_status);
                    const isCompleted = idx <= currentIdx;
                    return (
                      <div
                        key={s}
                        className="text-center"
                        style={{ zIndex: 2, width: "25%" }}
                      >
                        <div
                          className={`rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center shadow-sm ${isCompleted ? "bg-primary text-white" : "bg-white border text-muted"}`}
                          style={{ width: "32px", height: "32px" }}
                        >
                          {isCompleted ? <TiTick /> : idx + 1}
                        </div>
                        <small
                          className={`fw-medium ${isCompleted ? "text-dark" : "text-muted"}`}
                          style={{ fontSize: "0.7rem" }}
                        >
                          {s}
                        </small>
                      </div>
                    );
                  })}
                  <div
                    className="position-absolute top-50 start-0 translate-middle-y w-100"
                    style={{
                      height: "2px",
                      backgroundColor: "#e9ecef",
                      zIndex: 1,
                      left: "12.5%",
                      width: "75%",
                    }}
                  >
                    <div
                      className="bg-primary h-100 transition-all"
                      style={{
                        width: `${(statusFlow.indexOf(order.order_status) / 3) * 100}%`,
                        transition: "width 0.5s ease",
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Items Table Card */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3">
                <h6 className="fw-bold m-0 d-flex align-items-center gap-2">
                  <FiPackage className="text-primary" /> Products Ordered (
                  {order.items?.length})
                </h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead className=" ">
                      <tr>
                        <th className="border-0 px-4 py-3 text-white small">
                          PRODUCT
                        </th>
                        <th className="border-0 text-center text-white small">
                          QTY
                        </th>
                        <th className="border-0 text-end text-white small">
                          PRICE
                        </th>
                        <th className="border-0 text-end px-4 text-white small">
                          TOTAL
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, i) => (
                        <tr key={i}>
                          <td className="px-4 py-3">
                            <div className="d-flex align-items-center gap-3">
                              <img
                                src={
                                  item.product_image
                                    ? `${imgurl}${item.product_image}`
                                    : "/noimage.png"
                                }
                                alt=""
                                className="rounded border shadow-sm"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                }}
                              />
                              <div>
                                <div className="fw-bold text-dark small mb-0">
                                  {item.product_name}
                                </div>
                                {item.variant_json && (
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    {Object.entries(item.variant_json)
                                      .map(([k, v]) => `${k}: ${v}`)
                                      .join(", ")}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="text-center fw-medium small">
                            x{item.quantity}
                          </td>
                          <td className="text-end small">
                            <div className="fw-medium">
                              ₹{item.product_price}
                            </div>
                            <del className="text-muted smaller">
                              ₹{item.product_mrp}
                            </del>
                          </td>
                          <td className="text-end fw-bold px-4 small">
                            ₹{item.total_price}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card-footer bg-white border-0 py-4 px-4 text-end">
                <span className="text-muted small me-2">Grand Total:</span>
                <span className="fw-bold text-danger fs-4">
                  ₹{order.vendor_total}
                </span>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            {/* Customer & Payment Info Card */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 border-bottom pb-2">
                  <FiUser className="text-primary" /> Customer & Payment
                </h6>
                <div className="mb-3">
                  <label className="text-muted smaller d-block mb-1">
                    CUSTOMER NAME
                  </label>
                  <div className="fw-bold">{order.customer?.name}</div>
                  <div className="text-muted small">
                    {order.customer?.phone}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="text-muted smaller d-block mb-1">
                    ORDERED ON
                  </label>
                  <div className="fw-medium d-flex align-items-center gap-2 small">
                    <FiCalendar size={14} />{" "}
                    {new Date(order.created_at).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
                <div className="mb-0">
                  <label className="text-muted smaller d-block mb-1">
                    PAYMENT DETAILS
                  </label>
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="fw-bold small">
                      {order.payment_method?.toUpperCase()}
                    </span>
                    <span
                      className={`badge ${order.payment_status === "Paid" ? "bg-success" : "bg-warning text-dark"}`}
                      style={{ fontSize: "0.7rem" }}
                    >
                      {order.payment_status}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Card */}
            <div className="card border-0 shadow-sm overflow-hidden">
              <div className="bg-danger py-2 px-4 text-white small fw-bold d-flex align-items-center gap-2">
                <FiMapPin size={14} /> Shipping Information
              </div>
              <div className="card-body p-4">
                <div className="fw-bold mb-2">
                  {order.shipping_details?.fullName}
                </div>
                <div className="text-muted small lh-base mb-3">
                  {order.shipping_details?.address}
                  <br />
                  {order.shipping_details?.city},{" "}
                  {order.shipping_details?.state}
                  <br />
                  <span className="fw-bold text-dark">
                    {order.shipping_details?.pincode}
                  </span>
                </div>
                <div className="bg-light p-2 rounded small d-flex align-items-center gap-2">
                  <FiTruck size={14} className="text-primary" />
                  <span className="fw-medium">
                    {order.shipping_details?.phone}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .bg-primary-subtle { background-color: #e7f1ff !important; }
        .smaller { font-size: 0.65rem; font-weight: 700; letter-spacing: 0.5px; }
        .card { border-radius: 10px; }
        .btn { border-radius: 6px; font-size: 0.9rem; transition: all 0.2s; }
        .btn:hover:not(:disabled) { transform: translateY(-1px); }
      `}</style>
    </>
  );
}

export default OrderView;